/* components/ui/Chip.jsx — семейство тегов/чипов.
   Chip        — базовый тег, опц. кнопка удаления ×, tone via пропс.
   ChipList    — read-only набор chips (аллергены, типы кухонь); сворачивание +N.
   ChipEditor  — инлайн-редактор тегов: chips + поле ввода; Enter добавляет, × удаляет.
   FilterChip  — chip-переключатель фильтра (active/inactive).
   CardTypeChip— интерактивный chip выбора типа карты (Блюдо/Полуфабрикат).
   ХЕНДОФФ → frontend/components/common/. */

import React from 'react';
import { Icon } from './Icon.jsx';

  const { useState, useRef } = React;

  /* ── Chip ── */
  function Chip({ children, tone = 'neutral', icon, onRemove, removeLabel = 'Удалить', size = 'md', className }) {
    return (
      <span className={'chip chip--' + tone + ' chip--' + size + (className ? ' ' + className : '')}>
        {icon && <Icon name={icon} size={13} />}
        <span className="chip__label">{children}</span>
        {onRemove && (
          <button type="button" className="chip__remove" onClick={onRemove} aria-label={removeLabel}>
            <Icon name="close" size={12} />
          </button>
        )}
      </span>
    );
  }

  /* ── ChipList: read-only, со сворачиванием ── */
  function ChipList({ items = [], tone = 'neutral', max, icon, empty = '—', className }) {
    const [expanded, setExpanded] = useState(false);
    if (!items.length) return <span className="text-mute">{empty}</span>;
    const shown = max && !expanded ? items.slice(0, max) : items;
    const rest = max && !expanded ? items.length - max : 0;
    return (
      <span className={'chip-list cluster cluster-4' + (className ? ' ' + className : '')}>
        {shown.map((it, i) => {
          const label = typeof it === 'string' ? it : it.label;
          return <Chip key={i} tone={tone} icon={icon} size="sm">{label}</Chip>;
        })}
        {rest > 0 && (
          <button type="button" className="chip chip--neutral chip--sm chip--more" onClick={() => setExpanded(true)}>
            +{rest}
          </button>
        )}
      </span>
    );
  }

  /* ── ChipEditor ── */
  function ChipEditor({ value = [], onChange, placeholder = 'Добавить и Enter…', tone = 'accent', className }) {
    const [draft, setDraft] = useState('');
    const inputRef = useRef(null);
    const add = () => {
      const v = draft.trim();
      if (!v || value.includes(v)) { setDraft(''); return; }
      onChange && onChange([...value, v]);
      setDraft('');
    };
    const remove = (idx) => onChange && onChange(value.filter((_, i) => i !== idx));
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); add(); }
      else if (e.key === 'Backspace' && !draft && value.length) remove(value.length - 1);
    };
    return (
      <div className={'chip-editor' + (className ? ' ' + className : '')} onClick={() => inputRef.current && inputRef.current.focus()}>
        {value.map((v, i) => <Chip key={i} tone={tone} size="sm" onRemove={() => remove(i)}>{v}</Chip>)}
        <input ref={inputRef} className="chip-editor__input" value={draft}
          onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={add}
          placeholder={value.length ? '' : placeholder} />
      </div>
    );
  }

  /* ── FilterChip ──
     dismissible=false — чип ОДИНОЧНОГО выбора (переключатель вида: одна группа из
     нескольких). У выбранного нет «×»: снять выбор нельзя, только выбрать другой —
     крестик обещал бы действие, которого нет. title — полная подпись при наведении.
     Пробел между подписью и счётчиком — текстом: иначе имя кнопки для экранного
     диктора слипается («Блюда42»); визуально его съедает flex. */
  function FilterChip({ children, active = false, onToggle, count, icon, className, dismissible = true, title }) {
    return (
      <button type="button" aria-pressed={active} title={title}
        className={'filter-chip' + (active ? ' is-active' : '') + (dismissible ? '' : ' filter-chip--single')
          + (className ? ' ' + className : '')}
        onClick={onToggle}>
        {icon && <Icon name={icon} size={14} />}
        <span className="filter-chip__label">{children}</span>
        {count != null && <>{' '}<span className="filter-chip__count num">{count}</span></>}
        {active && dismissible && <Icon name="close" size={13} className="filter-chip__x" />}
      </button>
    );
  }

  /* ── CardTypeChip: выбор типа карты ── */
  function CardTypeChip({ type = 'dish', value, onChange, disabled }) {
    const opts = [
      { value: 'dish', label: 'Блюдо', icon: 'utensils' },
      { value: 'semi', label: 'Полуфабрикат', icon: 'box' },
    ];
    const cur = value || type;
    return (
      <div className="card-type-chip" role="radiogroup" aria-label="Тип карты">
        {opts.map((o) => (
          <button type="button" key={o.value} role="radio" aria-checked={cur === o.value} disabled={disabled}
            className={'card-type-chip__opt' + (cur === o.value ? ' is-active' : '')}
            onClick={() => onChange && onChange(o.value)}>
            <Icon name={o.icon} size={16} />
            <span>{o.label}</span>
          </button>
        ))}
      </div>
    );
  }

export { Chip };
export { ChipList };
export { ChipEditor };
export { FilterChip };
export { CardTypeChip };
